import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Trash2 } from 'lucide-react';

const ConfirmDeletePayrollModal = ({ isOpen, onClose, onConfirm, run, loading }) => {
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setConfirmText('');
    }
  }, [isOpen]);

  if (!isOpen || !run) return null;

  const targetText = 'DELETE';
  const isMatch = confirmText.trim() === targetText;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isMatch || loading) return;
    await onConfirm();
  };

  const runTitle = run.description || run.run_number || `Run #${run.id}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 relative">
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 text-red-600 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Delete Payroll Run</h3>
            <p className="text-xs text-red-600 font-medium">Critical Action Required</p>
          </div>
        </div>

        <div className="space-y-3 mb-6 text-sm text-gray-600">
          <p>
            You are about to delete <strong className="text-gray-900">{runTitle}</strong> (
            {run.run_number}).
          </p>

          {run.status === 'paid' ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs leading-relaxed">
              <strong>Note:</strong> This run is currently <strong>PAID</strong>. Deleting it will
              reverse all employee net payment records, update loan balances, and log balancing
              reversal entries in the Statement of Account.
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              This will remove the draft run and all generated payslips permanently.
            </p>
          )}

          <div className="pt-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              To confirm, type{' '}
              <span className="font-mono text-red-600 select-all font-bold">DELETE</span> below:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              disabled={loading}
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono text-sm uppercase tracking-wider"
              autoFocus
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isMatch || loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl shadow-md transition-all"
          >
            <Trash2 className="w-4 h-4" />
            {loading ? 'Deleting...' : 'Confirm Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDeletePayrollModal;
